/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect } from 'react'

import { useFormContext } from 'react-hook-form'

import { FormBlockerElement as FormBlockerElementProps } from '@cash-web/protos/squareup/franklin/form_blocker.pb'
import logger from '@cash-web/shared/util-logger'
import { DateInputElementWithFormBlockerDefaults } from '@cash-web/shared-feature-form'
import { useFlowContext } from '@cash-web/shared-flow-data-access-context'

import { AddressElement } from '../AddressElement/AddressElement'
import { AvatarElement } from '../AvatarElement/AvatarElement'
import { ButtonElement } from '../ButtonElement/ButtonElement'
import CallToActionElement from '../CallToActionElement/CallToActionElement'
import { CaptionedTileElement } from '../CaptionedTileElement/CaptionedTileElement'
import { CardElement } from '../CardElement/CardElement'
import { CashtagElement } from '../CashtagElement/CashtagElement'
import { CellDefaultElement } from '../CellDefaultElement/CellDefaultElement'
import { CheckboxElement } from '../CheckboxElement/CheckboxElement'
import { CopyableElementGroup } from '../CopyableElementGroup/CopyableElementGroup'
import { CustomizedCardElement } from '../CustomizedCardElement/CustomizedCardElement'
import { DetailRowElement } from '../DetailRowElement/DetailRowElement'
import { DividerElement } from '../DividerElement/DividerElement'
import { EmojiPickerElement } from '../EmojiPickerElement/EmojiPickerElement'
import { FixedSpacerElement } from '../FixedSpacerElement/FixedSpacerElement'
import { GiftCardElement } from '../GiftCardElement/GiftCardElement'
import { HeroElement } from '../HeroElement/HeroElement'
import { HeroHeaderElement } from '../HeroHeaderElement/HeroHeaderElement'
import { HeroNumericsHeaderElement } from '../HeroNumericsHeaderElement/HeroNumericsHeaderElement'
import { LegalTextElement } from '../LegalTextElement/LegalTextElement'
import { ListOrderedElement } from '../ListOrderedElement/ListOrderedElement'
import LocalImageElement from '../LocalImageElement/LocalImageElement'
import { MerchantTransactionElement } from '../MerchantTransactionElement/MerchantTransactionElement'
import { MerchantTransactionPickerElement } from '../MerchantTransactionPickerElement/MerchantTransactionPickerElement'
import { MoneyElement } from '../MoneyElement/MoneyElement'
import { MoneyInputElement } from '../MoneyInputElement/MoneyInputElement'
import { MultilineTextInputElement } from '../MultilineTextInputElement/MultilineTextInputElement'
import { NoticeElement } from '../NoticeElement/NoticeElement'
import { OptionPickerElement } from '../OptionPickerElement/OptionPickerElement'
import { PaymentPlanScheduleElement } from '../PaymentPlanScheduleElement/PaymentPlanScheduleElement'
import { PaymentPlanSummaryElement } from '../PaymentPlanSummaryElement/PaymentPlanSummaryElement'
import { ProfilePreviewElement } from '../ProfilePreviewElement/ProfilePreviewElement'
import RemoteImageElement from '../RemoteImageElement/RemoteImageElement'
import { SectionHeaderElement } from '../SectionHeaderElement/SectionHeaderElement'
import SelectableRowElement from '../SelectableRowElement/SelectableRowElement'
import SpacerElement from '../SpacerElement/SpacerElement'
import { TextElement } from '../TextElement/TextElement'
import { TextInputElement } from '../TextInputElement/TextInputElement'
import { TimelineElement } from '../TimelineElement/TimelineElement'
import { UpsellElement } from '../UpsellElement/UpsellElement'

interface Props {
  element: FormBlockerElementProps
  formSectionIndex: number
  shouldAutoFocus?: boolean
}

export interface FormBlockerAdditionalElementProps {
  formSectionIndex: number
  elementId?: string
  shouldAutoFocus?: boolean
}

type RegisterableElements = Omit<FormBlockerElementProps, 'id'>

export type FormBlockerElementConfig = {
  [key in keyof RegisterableElements]: {
    element: React.FC<NonNullable<RegisterableElements[key] & FormBlockerAdditionalElementProps>>
    isFocusable: boolean
  }
}

/* eslint sort-keys: "warn" */
export const implementedElements: FormBlockerElementConfig = {
  address_element: { element: AddressElement, isFocusable: true },
  avatar_element: { element: AvatarElement, isFocusable: false },
  button_element: { element: ButtonElement, isFocusable: false },
  call_to_action_element: { element: CallToActionElement, isFocusable: false },
  captioned_tile_element: { element: CaptionedTileElement, isFocusable: false },
  card_element: { element: CardElement, isFocusable: false },
  cashtag_element: { element: CashtagElement, isFocusable: true },
  cell_default_element: { element: CellDefaultElement, isFocusable: false },
  checkbox_element: { element: CheckboxElement, isFocusable: true },
  copyable_element_group: { element: CopyableElementGroup, isFocusable: false },
  customized_card_element: { element: CustomizedCardElement, isFocusable: false },
  date_input_element: { element: DateInputElementWithFormBlockerDefaults, isFocusable: true },
  detail_row_element: { element: DetailRowElement, isFocusable: false },
  divider_element: { element: DividerElement, isFocusable: false },
  emoji_picker_element: { element: EmojiPickerElement, isFocusable: false },
  family_upsell_element: { element: UpsellElement, isFocusable: false },
  fixed_spacer_element: { element: FixedSpacerElement, isFocusable: false },
  gift_card_element: { element: GiftCardElement, isFocusable: false },
  hero_element: { element: HeroElement, isFocusable: false },
  hero_header_element: { element: HeroHeaderElement, isFocusable: false },
  hero_numerics_header_element: { element: HeroNumericsHeaderElement, isFocusable: false },
  legal_text_element: { element: LegalTextElement, isFocusable: false },
  list_ordered_element: { element: ListOrderedElement, isFocusable: false },
  local_image_element: { element: LocalImageElement, isFocusable: false },
  merchant_transaction_element: { element: MerchantTransactionElement, isFocusable: false },
  merchant_transaction_picker_element: { element: MerchantTransactionPickerElement, isFocusable: false },
  money_element: { element: MoneyElement, isFocusable: false },
  money_input_element: { element: MoneyInputElement, isFocusable: true },
  multiline_text_input_element: { element: MultilineTextInputElement, isFocusable: true },
  notice_element: { element: NoticeElement, isFocusable: false },
  option_picker_element: { element: OptionPickerElement, isFocusable: true },
  payment_plan_schedule_element: { element: PaymentPlanScheduleElement, isFocusable: false },
  payment_plan_summary_element: { element: PaymentPlanSummaryElement, isFocusable: false },
  profile_preview_element: { element: ProfilePreviewElement, isFocusable: false },
  remote_image_element: { element: RemoteImageElement, isFocusable: false },
  section_header_element: { element: SectionHeaderElement, isFocusable: false },
  selectable_row_element: { element: SelectableRowElement, isFocusable: false },
  spacer_element: { element: SpacerElement, isFocusable: false },
  text_element: { element: TextElement, isFocusable: false },
  text_input_element: { element: TextInputElement, isFocusable: true },
  timeline_element: { element: TimelineElement, isFocusable: false },
  upsell_element: { element: UpsellElement, isFocusable: false },
}
/* eslint-disable sort-keys */

// Once an element has been seen in staging or production, and we've verified that
// it looks okay, remove it from this list.
const implementedElementsNotYetSeenInProduction: (keyof FormBlockerElementConfig)[] = [
  'captioned_tile_element',
  'card_element',
  'cashtag_element',
  'family_upsell_element',
  'gift_card_element',
  'hero_header_element',
  'hero_numerics_header_element',
  'list_ordered_element',
  'money_element',
  'multiline_text_input_element',
  'notice_element',
  'timeline_element',
]
function useLogIfElementSeenForFirstTimeEver(type: keyof FormBlockerElementConfig, element: FormBlockerElementProps) {
  const flow = useFlowContext()

  // This is being stored in a ref because we don't want new instances of the (same) object to trigger more logs.
  const elementRef = React.useRef(element)

  useEffect(() => {
    const elementJson = elementRef.current[type] ? JSON.stringify(elementRef.current[type]) : 'undefined'
    if (implementedElementsNotYetSeenInProduction.includes(type)) {
      logger.error(
        new Error(`${type} seen for the first time: ${flow.active?.blockerData?.id} flow. Element JSON: ${elementJson}`)
      )
    }
  }, [type, flow.active?.blockerData?.id])
}

export function FormBlockerElement({
  element: { id, ...element },
  formSectionIndex,
  shouldAutoFocus,
}: Props): React.ReactElement | null {
  const { register } = useFormContext()
  const type = Object.keys(element).find(key => element[key as keyof typeof element] != null) as
    | keyof FormBlockerElementConfig
    | undefined
  if (!type) {
    throw Error('Form Blocker - no element specified')
  }

  useLogIfElementSeenForFirstTimeEver(type, element)

  const Element = implementedElements[type]?.element as React.ComponentType<FormBlockerAdditionalElementProps>
  if (!Element) {
    throw Error(`Form Blocker - unimplemented element: ${type}`)
  }

  // if id is present and doesn't match the element type
  const shouldAddId = id != null && id.toLowerCase() !== type.replace('_', '')
  return (
    <>
      {shouldAddId && <input type="hidden" value={id} {...register(`${formSectionIndex}.id`)} />}
      <Element
        {...element[type]}
        formSectionIndex={formSectionIndex}
        elementId={id}
        shouldAutoFocus={shouldAutoFocus}
      />
    </>
  )
}
